/**
 * tripMapRoute.ts — the map half of "the map tells the same trip as the list".
 *
 * Pure helpers (no Google types) so the rules are unit-tested:
 *   • every stop wears the same letter and role colour in the list AND on
 *     the map (planner/roles.ts); each leg of the route is drawn in the
 *     colour of the day it's driven (Day 1 blue, Day 2 green …), the same
 *     colour as that day's heading in the list;
 *   • the drawn route passes through EVERY stop — dropped pins included —
 *     chunked to Google's 25-waypoint limit per request;
 *   • the road legs the map gets back become the panel's TripLeg figures;
 *   • one summary line ("1,050 km · 12 h 10 min", "via Princes Hwy").
 */

import type { TripLeg, TripStop } from '../types/trip';
import { DAY_COLORS, dayColor } from './tripDayColors';

/** Day colours: the planner's tp.day-1…7 tokens, one palette for the list
 *  and the map (utils/tripDayColors). */
export const TRIP_DAY_HEX = DAY_COLORS;

export function dayHex(dayIndex: number): string {
  return dayColor(Number.isFinite(dayIndex) && dayIndex > 0 ? Math.floor(dayIndex) : 0);
}

/**
 * Drive times are shown to the nearest 5 minutes. Google's estimate moves a
 * minute or two between requests; rounding once, at the source, means the
 * map, the list, the phone and the desktop all print the same figure.
 */
export function roundDriveMinutes(minutes: number): number {
  const m = Number(minutes);
  if (!Number.isFinite(m) || m <= 0) return 0;
  if (m < 5) return Math.max(1, Math.round(m));
  return Math.round(m / 5) * 5;
}

/** Google Directions allows 25 intermediate waypoints per request. */
export const MAX_DIRECTIONS_WAYPOINTS = 25;

/**
 * Split an ordered point list into Directions requests of at most
 * `maxWaypoints` intermediate points each. Consecutive chunks share their
 * boundary point, so the legs join up end to end and the leg count across all
 * chunks is always points.length − 1.
 */
export function chunkRoutePoints<T>(points: T[], maxWaypoints: number = MAX_DIRECTIONS_WAYPOINTS): T[][] {
  if (points.length < 2) return [];
  const size = Math.max(1, Math.floor(maxWaypoints)) + 2; // origin + waypoints + destination
  const chunks: T[][] = [];
  let start = 0;
  while (start < points.length - 1) {
    const end = Math.min(points.length - 1, start + size - 1);
    chunks.push(points.slice(start, end + 1));
    start = end;
  }
  return chunks;
}

/** Number of intermediate stops the route must pass through. */
export function waypointCount(pointCount: number): number {
  return Math.max(0, pointCount - 2);
}

export interface RoadLegFigure {
  /** metres, as Google returns it */
  distanceMeters: number;
  /** seconds, as Google returns it */
  durationSeconds: number;
  /** "via Monaro Hwy" for this leg, from its steps. */
  via?: string | null;
}

/**
 * Road legs from the map → the panel's TripLeg list. Returns null when the
 * routed legs don't line up one-to-one with the stops (a stale response, or a
 * route drawn for something other than this stop list), so the panel never
 * shows another trip's numbers.
 */
export function roadLegsForStops(
  stopIds: string[],
  figures: RoadLegFigure[]
): TripLeg[] | null {
  if (stopIds.length < 2 || figures.length !== stopIds.length - 1) return null;
  return figures.map((f, i) => {
    const leg: TripLeg = {
      fromId: stopIds[i],
      toId: stopIds[i + 1],
      distanceKm: Math.round((Number(f.distanceMeters) || 0) / 1000),
      driveMinutes: roundDriveMinutes((Number(f.durationSeconds) || 0) / 60),
    };
    if (f.via) leg.via = f.via;
    return leg;
  });
}

/** "1,050 km" — whole km, en-AU thousands separator. */
export function formatKm(km: number): string {
  return `${Math.round(km).toLocaleString('en-AU')} km`;
}

/** "12 h 10 min" / "45 min" / "3 h". Minutes are "min", never a bare "m"
 *  — next to "72 km" a bare "59 m" reads as metres. */
export function formatDuration(minutes: number): string {
  const total = Math.max(0, Math.round(minutes));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} h`;
  return `${h} h ${m} min`;
}

/** "1,050 km · 12 h 10 min" (drive time omitted when unknown). */
export function formatRouteSummary(km: number | undefined, minutes: number | undefined): string | null {
  if (typeof km !== 'number' || !(km > 0)) return null;
  const parts = [formatKm(km)];
  if (typeof minutes === 'number' && minutes > 0) parts.push(formatDuration(minutes));
  return parts.join(' · ');
}

/**
 * Google's route summaries ("M31 and A1", "Princes Hwy/A1") → one short
 * "via …" line. Duplicate roads across chunks collapse; at most two names.
 */
export function viaLabel(summaries: (string | null | undefined)[]): string | null {
  const names: string[] = [];
  for (const raw of summaries) {
    for (const part of String(raw || '').split(/\s+and\s+|\//)) {
      const name = part.trim();
      if (name && !names.some((n) => n.toLowerCase() === name.toLowerCase())) names.push(name);
    }
  }
  if (!names.length) return null;
  return `via ${names.slice(0, 2).join(' and ')}`;
}

export interface RouteStepLike {
  /** Google's HTML instruction, e.g. "Turn <b>left</b> onto <b>Princes Hwy</b>/<wbr/><b>A1</b>". */
  instructions?: string | null;
  /** metres */
  distanceMeters: number;
}

const ROUTE_SHIELD = /^[ABCMR]\d{1,3}$/i;

/** Shields that are better known by their road's name. */
const SHIELD_NAMES: Record<string, string> = { M31: 'Hume Hwy', M23: 'Federal Hwy' };
/** A highway-class name: what a driver calls the road. */
const MAJOR_ROAD = /\b(?:Hwy|Highway|Fwy|Freeway|Motorway|Mwy|Tollway|Pkwy|Parkway)\b/i;
/** Commemorative overlays ("Remembrance Driveway") that hide the real road. */
const OVERLAY_NAME = /\bRemembrance Driveway\b/i;

/**
 * "Princes Hwy/A1" → "Princes Hwy"; "Mahratta St/Monaro Hwy/B23" → "Monaro
 * Hwy" (the highway, not the town street it runs along); "Remembrance
 * Driveway/M31" → "Hume Hwy"; a bare "M1" stays "M1".
 */
function roadName(parts: string[]): string | null {
  const clean = parts.map((p) => p.replace(/&amp;/g, '&').replace(/\s+/g, ' ').trim()).filter(Boolean);
  if (!clean.length) return null;
  const named = clean.filter((p) => !ROUTE_SHIELD.test(p) && !OVERLAY_NAME.test(p));
  const major = named.find((p) => MAJOR_ROAD.test(p));
  if (major) return major;
  const shield = clean.find((p) => ROUTE_SHIELD.test(p));
  if (shield && SHIELD_NAMES[shield.toUpperCase()]) return SHIELD_NAMES[shield.toUpperCase()];
  return named[0] ?? shield ?? clean[0];
}

const boldNames = (html: string) => Array.from(html.matchAll(/<b>([^<]+)<\/b>/gi)).map((x) => x[1]);

/**
 * The road a step drives along, read from its instruction ("onto X", "on X",
 * "stay on X"). "…Continue to follow M1" wins: Google names the on-ramp
 * ("merge onto Citylink/M1") but the 164 km after it is the M1.
 */
export function stepRoad(instructions: string | null | undefined): string | null {
  const html = String(instructions || '');
  const follow = html.match(/Continue to follow\s+((?:<b>[^<]+<\/b>|[^<])+)/i);
  if (follow) {
    const names = boldNames(follow[1]);
    const plain = follow[1].replace(/<[^>]+>/g, '').split('/').map((x) => x.trim()).filter(Boolean);
    const got = roadName(names.length ? names : plain);
    if (got) return got;
  }
  const m = html.match(/\b(?:onto|on|stay on|continue on)\s+((?:<b>[^<]+<\/b>(?:\s*\/\s*(?:<wbr\s*\/?>)?\s*)?)+)/i);
  if (!m) return null;
  return roadName(boldNames(m[1]));
}

/**
 * The roads a route really uses, longest first: each step's distance is
 * credited to the road it drives along. Google's own route summary only
 * names a road or two from part of the trip, so a drive that turns inland at
 * Cann River still read "via M1 and Princes Hwy". Built from the steps, the
 * label names the roads the drawn line follows.
 */
export function roadsFromSteps(steps: RouteStepLike[]): { name: string; meters: number; first: number }[] {
  // `first` = where the road's longest stretch starts, so "via M1 and
  // Princes Hwy" reads in driving order even when a city block of Princes
  // Hwy comes before the freeway.
  const byName = new Map<string, { name: string; meters: number; first: number; longest: number }>();
  steps.forEach((st, i) => {
    const name = stepRoad(st.instructions);
    if (!name) return;
    const key = name.toLowerCase();
    const m = Number(st.distanceMeters) || 0;
    const cur = byName.get(key) ?? { name, meters: 0, first: i, longest: -1 };
    cur.meters += m;
    if (m > cur.longest) { cur.longest = m; cur.first = i; }
    byName.set(key, cur);
  });
  return Array.from(byName.values())
    .map(({ name, meters, first }) => ({ name, meters, first }))
    .sort((a, b) => b.meters - a.meters);
}

/**
 * "via Princes Hwy and Monaro Hwy": the two roads carrying the most of the
 * drive, in the order you meet them. A road has to carry at least 10% of the
 * distance to be named (15%), so a city on-ramp or a town's esplanade never
 * makes the label.
 */
export function viaFromSteps(steps: RouteStepLike[], max: number = 2): string | null {
  const total = steps.reduce((n, st) => n + (Number(st.distanceMeters) || 0), 0);
  if (!(total > 0)) return null;
  const roads = roadsFromSteps(steps).filter((r) => r.meters >= total * 0.15).slice(0, max);
  if (!roads.length) return null;
  roads.sort((a, b) => a.first - b.first);
  return `via ${roads.map((r) => r.name).join(' and ')}`;
}

/**
 * Split a whole-trip path into one path per leg, cutting at the vertex
 * nearest each stop (searching forward, so a route that doubles back is cut
 * in order). Used to colour each leg by its day when the route is one line.
 */
export function splitPathAtStops<P extends { lat: number; lng: number }>(
  path: P[],
  stops: { lat: number; lng: number }[]
): P[][] {
  if (path.length < 2) return [];
  if (stops.length < 3) return [path];
  const cuts: number[] = [0];
  let from = 0;
  for (let s = 1; s < stops.length - 1; s += 1) {
    let best = from;
    let bestD = Infinity;
    for (let i = from; i < path.length; i += 1) {
      const dLat = path[i].lat - stops[s].lat;
      const dLng = (path[i].lng - stops[s].lng) * Math.cos((stops[s].lat * Math.PI) / 180);
      const d = dLat * dLat + dLng * dLng;
      if (d < bestD) { bestD = d; best = i; }
    }
    cuts.push(best);
    from = best;
  }
  cuts.push(path.length - 1);
  const out: P[][] = [];
  for (let i = 1; i < cuts.length; i += 1) out.push(path.slice(cuts[i - 1], cuts[i] + 1));
  return out;
}

export interface ScreenRect { x: number; y: number; w: number; h: number }

export function rectsOverlap(a: ScreenRect, b: ScreenRect, gap: number = 0): boolean {
  return a.x < b.x + b.w + gap && b.x < a.x + a.w + gap && a.y < b.y + b.h + gap && b.y < a.y + a.h + gap;
}

/**
 * Where a leg's label goes: the first candidate spot along the leg (middle
 * first, then fanning out) whose label box stays clear of every pin and every
 * label already placed. null = no clear spot, so the label hides at this zoom
 * (the list still shows the leg). Pure screen-space geometry.
 */
export function pickLabelSpot(
  candidates: { x: number; y: number }[],
  size: { w: number; h: number },
  blockers: ScreenRect[],
  bounds?: { w: number; h: number },
  gap: number = 4
): number | null {
  for (let i = 0; i < candidates.length; i += 1) {
    const c = candidates[i];
    const box = { x: c.x - size.w / 2, y: c.y - size.h / 2, w: size.w, h: size.h };
    if (bounds && (box.x < 4 || box.y < 4 || box.x + box.w > bounds.w - 4 || box.y + box.h > bounds.h - 4)) continue;
    if (!blockers.some((b) => rectsOverlap(box, b, gap))) return i;
  }
  return null;
}

/** Fractions along a leg to try for its label, middle first. */
export const LABEL_FRACTIONS = [0.5, 0.42, 0.58, 0.34, 0.66, 0.26, 0.74];

/** Index of the point closest to `fraction` (0..1) of the way along a path. */
export function pointAtFraction<P extends { lat: number; lng: number }>(path: P[], fraction: number): P | null {
  if (!path.length) return null;
  if (path.length === 1) return path[0];
  const toRad = (d: number) => (d * Math.PI) / 180;
  const segs: number[] = [];
  let total = 0;
  for (let i = 1; i < path.length; i += 1) {
    const a = path[i - 1];
    const b = path[i];
    const x = toRad(b.lng - a.lng) * Math.cos(toRad((a.lat + b.lat) / 2));
    const y = toRad(b.lat - a.lat);
    const d = Math.sqrt(x * x + y * y);
    segs.push(d);
    total += d;
  }
  const target = total * Math.max(0, Math.min(1, fraction));
  let run = 0;
  for (let i = 0; i < segs.length; i += 1) {
    if (run + segs[i] >= target) return path[i + 1];
    run += segs[i];
  }
  return path[path.length - 1];
}

/** What a stop is on the trip — the list's Start / Overnight / Via /
 *  Destination, which the pins repeat as a shoulder badge (or, for a via
 *  stop, a smaller hollow pin). */
export type PinRole = 'start' | 'overnight' | 'via' | 'end';

/** Pin box in CSS px (the SVG includes a 4px margin for the selection halo). */
export const PIN_WIDTH = 34;
export const PIN_HEIGHT = 38;
/** A via pin is drawn at this scale of a full pin, from the same tip. */
export const VIA_PIN_SCALE = 0.76;

/** The pin's visible head (the part that covers the map) in px, relative to
 *  its tip — what labels and leg chips keep clear of. */
export function pinHeadBox(role: PinRole | undefined, tip: { x: number; y: number }): ScreenRect {
  const k = role === 'via' ? VIA_PIN_SCALE : 1;
  const w = Math.round(28 * k);
  const h = Math.round(36 * k);
  return { x: tip.x - w / 2, y: tip.y - h, w, h };
}

// Role glyphs on a 16-unit grid — the same shapes as RoleGlyph in the list.
const BADGE_GLYPH: Record<'start' | 'overnight' | 'end', (c: string) => string> = {
  start: (c) =>
    `<path d="M4 14.5V1.75" stroke="${c}" stroke-width="2" stroke-linecap="round"/>` +
    `<path d="M4.5 2.25h8.25l-2 2.75 2 2.75H4.5z" fill="${c}"/>`,
  overnight: (c) =>
    `<path d="M1.75 13.5V3.5M1.75 10.25h12.5v3.25M1.75 8.5h3.5" stroke="${c}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>` +
    `<path d="M6 6.5h6.25a2 2 0 0 1 2 2v1H6z" fill="${c}"/>`,
  end: (c) =>
    `<path d="M3 14.5V1.5" stroke="${c}" stroke-width="2" stroke-linecap="round"/>` +
    `<path d="M3.75 2h9.5v7h-9.5z" stroke="${c}" stroke-width="1.5" fill="none"/>` +
    `<path d="M3.75 2h3.2v2.33h-3.2zM10.05 2h3.2v2.33h-3.2zM6.95 4.33h3.1v2.34h-3.1zM3.75 6.67h3.2V9h-3.2zM10.05 6.67h3.2V9h-3.2z" fill="${c}"/>`,
};

/**
 * The lettered SVG pin for a stop, anchored at its tip, in the given colour
 * (the planner passes the stop's role colour, so pin C is row C). The pin says what the stop is, like the list:
 *   start — a flag badge on its shoulder
 *   overnight — a bed badge
 *   destination — a chequered-flag badge
 *   via — a smaller, hollow pin (white with a day-colour ring), no badge
 * Selected pins get a white halo ring; hovered pins a soft one. The SVG keeps
 * the same box whatever the state or role, so a pin never jumps.
 */
export function stopPinSvg(
  label: string,
  fill: string,
  opts: { selected?: boolean; highlighted?: boolean; role?: PinRole } = {}
): { svg: string; width: number; height: number; anchor: 'tip' } {
  const via = opts.role === 'via';
  const baseFont = label.length > 2 ? 9 : label.length > 1 ? 11 : 13;
  const pinPath =
    'M15 39c-.6 0-1.1-.3-1.4-.8C9 31.6 2 23.4 2 14.5 2 7 7.8 1.5 15 1.5S28 7 28 14.5c0 8.9-7 17.1-11.6 23.7-.3.5-.8.8-1.4.8z';
  // A via pin is the same teardrop scaled about its tip, so tips line up.
  const shrink = via ? ` transform="translate(15 39) scale(${VIA_PIN_SCALE}) translate(-15 -39)"` : '';
  const halo = opts.selected
    ? `<path d="${pinPath}"${shrink} fill="none" stroke="${fill}" stroke-opacity="0.3" stroke-width="8" stroke-linejoin="round"/>` +
      `<path d="${pinPath}"${shrink} fill="#ffffff" stroke="#ffffff" stroke-width="5" stroke-linejoin="round"/>`
    : opts.highlighted
      ? `<path d="${pinPath}"${shrink} fill="none" stroke="${fill}" stroke-opacity="0.25" stroke-width="7" stroke-linejoin="round"/>`
      : '';
  const body = via
    ? `<path d="${pinPath}"${shrink} fill="#ffffff" stroke="${fill}" stroke-width="3.2"/>`
    : `<path d="${pinPath}" fill="${fill}" stroke="#ffffff" stroke-width="2.25"/>`;
  // Head centre: (15, 14.5) on a full pin; scaled toward the tip on a via pin.
  const textY = via ? Math.round((39 - (39 - 19) * VIA_PIN_SCALE) * 10) / 10 : 19;
  const fontSize = via ? Math.max(9, baseFont - 2) : baseFont;
  const text = `<text x="15" y="${textY}" text-anchor="middle" font-family="Work Sans, Arial, sans-serif" font-size="${fontSize}" font-weight="700" fill="${via ? fill : '#ffffff'}">${escapeXml(label)}</text>`;
  const role = opts.role;
  const badge =
    role === 'start' || role === 'overnight' || role === 'end'
      ? `<g data-badge="${role}"><circle cx="27" cy="3.5" r="7" fill="#ffffff" stroke="${fill}" stroke-width="1.25"/>` +
        `<g transform="translate(22.5 -1) scale(0.5625)">${BADGE_GLYPH[role](fill)}</g></g>`
      : '';
  const svg = [
    // viewBox bottom = the tip (y 39), so the marker anchors on the place.
    `<svg xmlns="http://www.w3.org/2000/svg" width="${PIN_WIDTH}" height="${PIN_HEIGHT}" viewBox="-4 -4 38 43" overflow="visible" aria-hidden="true" focusable="false">`,
    halo,
    body,
    text,
    badge,
    '</svg>',
  ].join('');
  return { svg, width: PIN_WIDTH, height: PIN_HEIGHT, anchor: 'tip' };
}

/**
 * Nudge overlapping pins apart (screen pixels) so no number hides under
 * another — Lakes Entrance and a pin dropped 40 km away sit on top of each
 * other at the whole-trip zoom. Pairs closer than `minDist` are pushed apart
 * along the line between them (side by side when they coincide), a few
 * relaxation passes, each offset capped. Returns an offset per point.
 */
export function spreadOverlapping(
  points: { x: number; y: number }[],
  minDist: number = 30,
  maxOffset: number = minDist * 1.5
): { dx: number; dy: number }[] {
  const pos = points.map((p) => ({ x: p.x, y: p.y }));
  for (let pass = 0; pass < 8; pass += 1) {
    let moved = false;
    for (let i = 0; i < pos.length; i += 1) {
      for (let j = i + 1; j < pos.length; j += 1) {
        let vx = pos[j].x - pos[i].x;
        let vy = pos[j].y - pos[i].y;
        let d = Math.sqrt(vx * vx + vy * vy);
        if (d >= minDist - 0.01) continue;
        if (d < 0.01) { vx = 1; vy = 0; d = 1; } // same spot: side by side, in list order
        const push = (minDist - d) / 2;
        const ux = vx / d;
        const uy = vy / d;
        pos[i].x -= ux * push; pos[i].y -= uy * push;
        pos[j].x += ux * push; pos[j].y += uy * push;
        moved = true;
      }
    }
    if (!moved) break;
  }
  return pos.map((p, i) => {
    let dx = p.x - points[i].x;
    let dy = p.y - points[i].y;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len > maxOffset) { dx = (dx / len) * maxOffset; dy = (dy / len) * maxOffset; }
    return { dx: Math.round(dx * 10) / 10, dy: Math.round(dy * 10) / 10 };
  });
}

/** A leg needs this many screen px before it gets a label at all: a shorter
 *  leg can't hold "72 km · 1 h" clear of its own two pins. */
export const MIN_LABELLED_LEG_PX = 120;

/** Distinct driving days on the trip, in order — the map's colour key. */
export function daysInTrip(dayIndexes: (number | undefined)[]): number[] {
  return Array.from(new Set(dayIndexes.map((d) => (typeof d === 'number' && d >= 0 ? Math.floor(d) : 0)))).sort((a, b) => a - b);
}

function escapeXml(s: string): string {
  return s.replace(/[<>&"']/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&apos;' }[c] as string));
}

/** The selected stop and the stops either side of it — what a phone frames
 *  when a stop is picked, so the legs in and out of it stay on screen. */
export function neighbourhood(index: number, count: number): number[] {
  if (index < 0 || index >= count) return [];
  return [index - 1, index, index + 1].filter((i) => i >= 0 && i < count);
}

/**
 * A stop's name beside its pin: right of the pin head first (where a reader
 * looks next), then left, else hidden at this zoom. Returns the label box or
 * null. `pin` is the pin's screen box.
 */
export function placeNameLabel(
  pin: ScreenRect,
  size: { w: number; h: number },
  blockers: ScreenRect[],
  bounds?: { w: number; h: number },
  gap: number = 2
): ScreenRect | null {
  const y = pin.y + Math.round((pin.h * 0.42) - size.h / 2);
  const tries: ScreenRect[] = [
    { x: pin.x + pin.w + gap, y, w: size.w, h: size.h },
    { x: pin.x - gap - size.w, y, w: size.w, h: size.h },
  ];
  for (const box of tries) {
    if (bounds && (box.x < 2 || box.y < 2 || box.x + box.w > bounds.w - 2 || box.y + box.h > bounds.h - 2)) continue;
    if (!blockers.some((b) => rectsOverlap(box, b, 2))) return box;
  }
  return null;
}

/** Rough km between two nearby points (equirectangular; fine under ~50 km). */
export function snapDistanceKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const kx = 111.32 * Math.cos(((a.lat + b.lat) / 2) * (Math.PI / 180));
  return Math.hypot((b.lat - a.lat) * 110.57, (b.lng - a.lng) * kx);
}

/**
 * The trip's "via" line from each leg's own: every road the legs name, in
 * the order you drive them, no repeats, at most `max`. So the summary and the
 * legs in the list name the same roads ("via Citylink, Princes Hwy and Monaro
 * Hwy"), where Google's whole-route summary named two roads from part of it.
 */
export function tripVia(legVias: (string | null | undefined)[], max: number = 3): string | null {
  const names: string[] = [];
  for (const raw of legVias) {
    const body = String(raw || '').replace(/^\s*via\s+/i, '');
    for (const part of body.split(/\s*,\s*|\s+and\s+/)) {
      const name = part.trim();
      if (name && !names.some((n) => n.toLowerCase() === name.toLowerCase())) names.push(name);
    }
  }
  if (!names.length) return null;
  const shown = names.slice(0, max);
  if (shown.length === 1) return `via ${shown[0]}`;
  return `via ${shown.slice(0, -1).join(', ')} and ${shown[shown.length - 1]}`;
}

/**
 * Where the base map prints a stop's own town name: beside the town's dot,
 * right of it or left of it. Leg labels keep out of both, so "72 km" never
 * lands on "Lakes Entrance" and clips it to "Entrance". `at` is the stop's
 * screen point; the width is a measured guess from the name (13px bold).
 */
export function townLabelZones(at: { x: number; y: number }, name: string): ScreenRect[] {
  const w = Math.min(180, Math.max(40, Math.round(name.length * 7.6) + 10));
  const h = 20;
  const y = at.y - h / 2;
  return [
    { x: at.x + 4, y, w, h },
    { x: at.x - 4 - w, y, w, h },
  ];
}

/**
 * The trip view's quiet base map: parks, bush and water pulled back to soft
 * tints, state and country names off (a route split "AUSTR LIAN CAPI AL"),
 * no POIs. Town names stay, a shade lighter, so the trip's own pins, legs
 * and labels are the loudest things on the map. Google styler syntax.
 */
export const QUIET_TRIP_MAP_STYLE: { featureType?: string; elementType?: string; stylers: Record<string, string | number>[] }[] = [
  { featureType: 'administrative.country', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.province', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.province', elementType: 'geometry.stroke', stylers: [{ color: '#b9c3d0' }, { weight: 1 }] },
  { featureType: 'administrative.neighborhood', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#4a5a70' }] },
  { featureType: 'administrative.locality', elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }, { weight: 3 }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ visibility: 'on' }, { saturation: -55 }, { lightness: 38 }] },
  { featureType: 'landscape.natural', elementType: 'geometry', stylers: [{ saturation: -75 }, { lightness: 30 }] },
  { featureType: 'landscape.man_made', elementType: 'geometry', stylers: [{ saturation: -80 }, { lightness: 10 }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#d6e4ef' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#7d93ab' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ saturation: -100 }, { lightness: 20 }] },
  { featureType: 'road', elementType: 'labels.icon', stylers: [{ saturation: -100 }, { lightness: 15 }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#6b7a90' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
];

/** The map-type id the trip view registers its quiet style under. */
export const QUIET_TRIP_MAP_TYPE = 'tp_trip_quiet';

/**
 * The same quiet map with the base map's own town names off. Raster town
 * names can't step aside for a pin, so below TOWN_LABEL_MAX_ZOOM the trip
 * view prints town names itself (see mapLabels.ts), clear of its pins, its
 * route and its day chips.
 */
export const QUIET_TRIP_MAP_STYLE_NO_TOWNS: typeof QUIET_TRIP_MAP_STYLE = [
  ...QUIET_TRIP_MAP_STYLE.filter((r) => r.featureType !== 'administrative.locality'),
  { featureType: 'administrative.locality', elementType: 'labels', stylers: [{ visibility: 'off' }] },
];
export const QUIET_TRIP_MAP_TYPE_NO_TOWNS = 'tp_trip_quiet_nt';
/** Up to this zoom the map draws its own town names; past it, Google's. */
export const TOWN_LABEL_MAX_ZOOM = 9.5;

/**
 * The leg the map lights up: the one hovered in the list, else the drive
 * INTO the picked stop (the first stop has none, so the drive out of it).
 * Returned as the id of the stop the leg drives to; null = nothing lit.
 */
export function emphasisedLegToId(
  stopIds: string[],
  selectedId: string | null | undefined,
  hoveredLegToId: string | null | undefined
): string | null {
  if (hoveredLegToId && stopIds.indexOf(hoveredLegToId) > 0) return hoveredLegToId;
  if (!selectedId) return null;
  const i = stopIds.indexOf(selectedId);
  if (i < 0 || stopIds.length < 2) return null;
  return i === 0 ? stopIds[1] : selectedId;
}

/** One map chip per driving day: which legs (by index) it covers and its
 *  totals — the same sums the list's Day headers print. */
export interface DayChip {
  day: number;
  legIndexes: number[];
  distanceKm: number;
  driveMinutes: number;
}

/**
 * Group road legs into days by the day their destination stop is reached
 * (`stopDays[i + 1]` for leg i). Totals are sums of the per-leg figures the
 * panel shows, so "Day 2 · 730 km" on the map is the Day 2 header's figure.
 */
export function dayChips(
  stopDays: (number | undefined)[],
  legs: { distanceKm?: number; driveMinutes?: number }[]
): DayChip[] {
  const out: DayChip[] = [];
  legs.forEach((leg, i) => {
    const day = Math.max(0, Math.floor(stopDays[i + 1] ?? 0));
    let chip = out.find((c) => c.day === day);
    if (!chip) {
      chip = { day, legIndexes: [], distanceKm: 0, driveMinutes: 0 };
      out.push(chip);
    }
    chip.legIndexes.push(i);
    chip.distanceKm += leg.distanceKm ?? 0;
    chip.driveMinutes += leg.driveMinutes ?? 0;
  });
  return out.sort((a, b) => a.day - b.day);
}

/**
 * Where a chip goes beside a line, with a leader back to it: try each anchor
 * point on the line, on each side (perpendicular to the line there), at two
 * distances. The chip box must stay clear of `blockers` (pins, names, other
 * chips), of the route itself (`linePts`, sampled screen points) and inside
 * the map. Returns the anchor, the chip box and the leader's end on the box,
 * or null when nowhere is clear at this zoom.
 */
export function placeChipBesideLine(
  anchors: { x: number; y: number; nx: number; ny: number }[],
  size: { w: number; h: number },
  blockers: ScreenRect[],
  linePts: { x: number; y: number }[],
  area: { w: number; h: number },
  opts: { gaps?: number[]; margin?: number } = {}
): { anchor: { x: number; y: number }; box: ScreenRect; leaderEnd: { x: number; y: number } } | null {
  const gaps = opts.gaps ?? [18, 32];
  const margin = opts.margin ?? 8;
  for (const gap of gaps) {
    for (const a of anchors) {
      for (const side of [1, -1]) {
        const nx = a.nx * side;
        const ny = a.ny * side;
        // Distance from the anchor to the box centre along the normal.
        const half = Math.abs(nx) * (size.w / 2) + Math.abs(ny) * (size.h / 2);
        const cx = a.x + nx * (gap + half);
        const cy = a.y + ny * (gap + half);
        const box = { x: cx - size.w / 2, y: cy - size.h / 2, w: size.w, h: size.h };
        if (box.x < margin || box.y < margin || box.x + box.w > area.w - margin || box.y + box.h > area.h - margin) continue;
        if (blockers.some((b) => rectsOverlap(box, b, 4))) continue;
        if (linePts.some((p) => p.x > box.x - 5 && p.x < box.x + box.w + 5 && p.y > box.y - 5 && p.y < box.y + box.h + 5)) continue;
        return { anchor: { x: a.x, y: a.y }, box, leaderEnd: { x: a.x + nx * gap, y: a.y + ny * gap } };
      }
    }
  }
  return null;
}
